// =====================================================================
// components/SignInScreen.tsx
// Returning members — email OTP (magic link still works as fallback).
// =====================================================================

import { useEffect, useState } from 'react';
import { EmailOtpVerify } from '@/components/auth/EmailOtpVerify';
import { db } from '@/lib/db';
import { clearAllInviteState } from '@/lib/invite-token';
import { InstallAppTip } from '@/components/shared/InstallAppTip';

interface Props {
  onBack?: () => void;
}

export function SignInScreen({ onBack }: Props) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const unsubscribe = db.auth.onChange((member) => {
      if (member) clearAllInviteState();
    });
    return unsubscribe;
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await db.auth.signIn(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-ink">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center font-serif text-3xl font-bold tracking-widest">BAG TAG</div>
          <EmailOtpVerify
            email={email.trim().toLowerCase()}
            onVerify={async (code) => {
              await db.auth.verifyEmailOtp(email, code);
            }}
            onResend={async () => {
              await db.auth.signIn(email.trim().toLowerCase());
            }}
            onChangeEmail={() => {
              setSent(false);
              setEmail('');
              setError(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6 text-ink">
      <div className="w-full max-w-sm">
        <div className="mb-12 text-center">
          <div className="mb-2 font-serif text-3xl font-bold tracking-widest">BAG TAG</div>
          <div className="text-xs italic text-mute">
            Independent app for golf members.<br />
            Not affiliated with any country club.
          </div>
        </div>

        <InstallAppTip className="mb-6" />

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-semibold">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              className="w-full rounded-lg border-2 border-line bg-paper px-4 py-3 focus:border-gold focus:outline-none"
            />
          </div>

          {error && (
            <div className="rounded bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!email || submitting}
            className="w-full rounded-lg bg-gold py-3 font-bold tracking-wider text-navy disabled:opacity-50"
          >
            {submitting ? 'SENDING…' : 'SEND SIGN-IN CODE'}
          </button>
        </form>

        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mt-8 w-full text-center text-sm text-gray-500 underline"
          >
            Back
          </button>
        )}

        <p className="mt-4 text-center text-xs text-gray-500">
          New here? You need an invitation from an existing member first.
        </p>
      </div>
    </div>
  );
}
